import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { ExternalLink, Link2 } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import * as SimpleIcons from 'react-icons/si';
import * as FontAwesome from 'react-icons/fa';
import * as MaterialDesign from 'react-icons/md';
import { EmptyState } from '../ui/EmptyState';
import { CardSkeleton } from '../ui/Skeleton';

interface SharedLink {
  id: string;
  titulo: string;
  descripcion: string | null;
  url: string;
  icono: string;
  orden: number;
}

export function LinksViewer() {
  const { profile } = useAuth();
  const [links, setLinks] = useState<SharedLink[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile) loadLinks();
  }, [profile]);

  useEffect(() => {
    const channel = supabase
      .channel('shared_links_viewer_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'shared_links',
        },
        () => {
          if (profile) loadLinks();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile]);

  const loadLinks = async () => {
    try {
      // Docentes/admins see all active links
      if (profile?.role !== 'alumno') {
        const { data, error } = await supabase
          .from('shared_links')
          .select('*')
          .eq('activo', true)
          .order('orden', { ascending: true });
        if (error) throw error;
        setLinks(data || []);
        return;
      }

      // Alumnos: get all active enrolled courses
      const { data: enrollments } = await supabase
        .from('course_enrollments')
        .select('course_id')
        .eq('alumno_id', profile!.id)
        .eq('status', 'active');

      const courseIds = (enrollments || []).map((e) => e.course_id);

      // Global links (apply_to_all_courses = true)
      const { data: globalLinks } = await supabase
        .from('shared_links')
        .select('*')
        .eq('activo', true)
        .eq('apply_to_all_courses', true)
        .order('orden', { ascending: true });

      // Course-specific links for all enrolled courses
      let courseLinks: SharedLink[] = [];
      if (courseIds.length > 0) {
        const { data: assigned } = await supabase
          .from('shared_links')
          .select('*, link_course_assignments!inner(course_id)')
          .eq('activo', true)
          .in('link_course_assignments.course_id', courseIds)
          .order('orden', { ascending: true });
        courseLinks = (assigned || []).map(({ link_course_assignments: _lca, ...link }) => link as SharedLink);
      }

      // Merge + deduplicate, preserving orden sort
      const seen = new Set<string>();
      const merged: SharedLink[] = [];
      for (const l of [...(globalLinks || []), ...courseLinks]) {
        if (!seen.has(l.id)) { seen.add(l.id); merged.push(l); }
      }
      merged.sort((a, b) => a.orden - b.orden);
      setLinks(merged);
    } catch (error) {
      console.error('Error loading links:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconString: string) => {
    const [library, iconName] = iconString.includes(':')
      ? iconString.split(':')
      : ['lucide', iconString];

    try {
      if (library === 'si') {
        return (SimpleIcons as any)[iconName] || LucideIcons.Link;
      } else if (library === 'fa') {
        return (FontAwesome as any)[iconName] || LucideIcons.Link;
      } else if (library === 'md') {
        return (MaterialDesign as any)[iconName] || LucideIcons.Link;
      } else {
        return (LucideIcons as any)[iconName] || LucideIcons.Link;
      }
    } catch {
      return LucideIcons.Link;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div>
          <h1 className="text-display text-3xl font-bold text-white mb-2">Links Útiles</h1>
          <p className="text-muted">Cargando recursos…</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <div>
        <h1 className="text-display text-3xl font-bold text-white mb-2">Links Útiles</h1>
        <p className="text-muted">Accede a recursos y herramientas importantes</p>
      </div>

      {links.length === 0 ? (
        <div className="glass-surface rounded-xl border border-white/[0.06]">
          <EmptyState
            icon={Link2}
            title="No hay links disponibles"
            description="Los profesores aún no han compartido ningún link. Vuelve más tarde."
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {links.map((link) => {
            const Icon = getIcon(link.icono);
            return (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-surface rounded-xl p-6 border border-white/[0.06] hover:border-cubus-lime/40 transition-all duration-300 group"
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-3 bg-cubus-lime/10 rounded-lg group-hover:bg-cubus-lime/20 transition-colors">
                      <Icon className="w-6 h-6 text-cubus-lime" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cubus-lime transition-colors">
                        {link.titulo}
                      </h3>
                      {link.descripcion && (
                        <p className="text-muted text-sm line-clamp-2">
                          {link.descripcion}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-auto pt-4 border-t border-white/[0.08]">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-cubus-lime group-hover:underline flex items-center gap-1">
                        Abrir link
                        <ExternalLink className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
